import { GetStaticProps } from "next";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Stack,
} from "@chakra-ui/react";
import { BaseStack } from "goi_common/src/design-system";
import { baseColors } from "goi_common/src/design-system";
import {
  call,
  ColorIcon,
  colors,
  customEvent,
  getCdnImageSrc,
  Image,
  RenewalFooter,
  responsive,
} from "goi_common";
import { useRouter } from "next/router";

// PC-only 오버라이드 (모바일은 className으로 처리)
const PC_BODY = {
  fontSize: { lg: "18px" },
  lineHeight: { lg: "29px" },
  letterSpacing: { lg: "-0.2px" }, // 모바일 -0.1px → PC -0.2px
} as const;

const PC_SECTION_TITLE = {
  fontSize: { lg: "24px" },
  lineHeight: { lg: "32px" },
  letterSpacing: { lg: "-0.3px" }, // 모바일 -0.2px → PC -0.3px
} as const;

const PC_CRITERION_TITLE = {
  fontSize: { lg: "18px" },
  lineHeight: { lg: "29px" },
  // letterSpacing -0.2px 동일 → 오버라이드 불필요
} as const;

const PC_TABLE_LABEL = {
  fontSize: { lg: "18px" },
  lineHeight: { lg: "26px" },
  letterSpacing: { lg: "-0.2px" },
} as const;

const PC_TABLE_CELL = {
  fontSize: { lg: "17px" },
  lineHeight: { lg: "24px" },
  letterSpacing: { lg: "-0.2px" },
} as const;

const PC_FAQ = {
  fontSize: { lg: "18px" },
  lineHeight: { lg: "26px" },
  letterSpacing: { lg: "-0.2px" },
  fontWeight: { lg: "600" }, // 모바일 500(m) → PC 600(sb)
} as const;

const DIVIDER = <Box width="56px" height="1px" bg="#dddedf" flexShrink={0} />;
const TRUST_DIVIDER = (
  <Box height="1px" bg="#dddedf" width="100%" flexShrink={0} />
);

// ── 신뢰 섹션 아이콘 ──────────────────────────────────────────────

const IconKB = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
  >
    <rect width="36" height="36" fill="white" />
    <path
      d="M27.9016 14.6463L27.7248 14.5335C27.1212 13.9314 26.2797 13.6707 25.1563 13.7363C24.2203 13.7927 23.4779 14.1646 22.9109 14.5091C22.9109 14.5 22.9109 14.4924 22.9109 14.4832C22.9109 14.1906 22.9444 13.872 22.9764 13.564C23.0084 13.25 23.0435 12.9314 23.0435 12.6296C23.0435 12.3278 23.0084 12.029 22.9032 11.779C22.8758 11.715 22.8179 11.6769 22.7432 11.6769C22.4414 11.683 21.8499 11.8903 21.7066 12.0153L21.6289 12.186C21.6243 12.5214 21.5496 13.4558 21.4749 13.5701C21.478 13.5656 21.4353 13.6829 21.4353 13.6829C21.3072 15.0183 21.3057 16.192 21.4231 17.1996C21.4368 17.3003 21.6304 17.4512 21.7813 17.506C21.9459 17.567 22.4596 17.4283 22.7569 17.317C22.7523 17.317 22.8682 17.2987 22.8682 17.2987C22.9612 17.2881 23.0176 17.2103 23.013 17.1021C23.0115 17.1006 23.0206 16.8125 23.0206 16.8125C23.234 15.942 24.0999 14.904 25.1258 14.7485C25.705 14.657 26.1623 14.8049 26.5236 15.1997C26.5724 15.2698 26.6776 15.8963 26.2965 16.6158C26.0114 17.1478 25.4428 17.5975 24.6959 17.878C23.9886 18.1478 23.2096 18.256 22.2478 18.2225C22.2081 18.1966 21.3469 17.6143 21.3469 17.6143C20.5832 17.0792 19.6365 16.4116 18.7783 16.0869C18.6274 16.0289 18.3576 15.8033 18.1945 15.6692L18.1061 15.596C17.6106 15.1982 16.6671 14.5701 15.911 14.0656C15.911 14.0656 15.5695 13.8354 15.504 13.7927C15.504 13.7637 15.504 13.7073 15.504 13.7073C15.5268 13.6829 16.0024 13.3979 16.0024 13.3979C16.6686 13.0076 16.9765 12.8171 17.0421 12.718C17.0283 12.7333 17.1305 12.6662 17.1305 12.6662C17.1366 12.6631 18.7814 11.7317 18.7814 11.7317C20.3667 10.8613 22.1624 9.87202 23.106 8.89643C23.1075 8.89643 23.1517 8.74704 23.1517 8.74704L23.1715 8.61442C23.234 8.40558 23.2371 8.24553 23.1852 8.05955C23.1715 8.0062 23.1227 7.94523 23.0572 7.93303C22.3209 7.85986 21.3713 8.3004 20.5054 9.11289C20.3103 9.29581 20.1457 9.38575 19.9734 9.47874L19.8408 9.55343C18.8881 10.0946 16.7402 11.4284 15.6152 12.1479C15.6899 10.9391 15.9125 9.2196 16.1884 7.78517L16.1579 7.61444L16.0421 7.39798L15.9552 7.24249L15.9293 7.2364C15.7052 7.12207 15.5223 7.16932 15.3866 7.20286L15.1625 7.28518L14.8622 7.39493C14.8028 7.40713 14.7616 7.44371 14.7387 7.50011C14.2022 8.79735 13.754 10.9315 13.6473 12.6403C12.5741 11.9574 11.5833 11.468 11.0955 11.2302L11.0818 11.2226L11.0239 11.2104C10.8486 11.1876 10.6351 11.0595 10.4309 10.9345C10.408 10.9223 10.0224 10.6708 10.0224 10.6708L9.39889 10.2714L9.23426 10.2333C8.96597 10.279 8.45226 10.6251 8.29982 10.8613C8.28 10.8918 8.27086 10.9254 8.27086 10.9604C8.27086 11.0092 8.2861 11.0595 8.29982 11.1083L8.32726 11.2348L8.35622 11.311C8.655 11.6174 9.40956 12.1281 10.1489 12.5259L11.0056 12.9802L12.318 13.6967L12.5406 13.875L12.6946 13.9634C12.6946 13.9634 12.7129 13.9741 12.7235 13.9802C12.3744 14.2149 10.3364 15.5747 10.3364 15.5747L9.08182 16.407C8.96292 16.4878 8.4675 16.7088 8.35775 16.753C8.08641 16.8643 7.87909 17.0335 7.79525 17.2179L7.76172 17.2911L7.79525 17.3384L7.89129 17.4115L8.01934 17.4954L8.15805 17.5899L8.28915 17.6112C8.51018 17.6188 8.8364 17.4939 9.44767 17.2256C10.0086 16.9771 11.533 16.0579 12.1854 15.6646L12.4629 15.4985C12.5787 15.5076 12.7037 15.4116 12.818 15.3262C12.9491 15.221 13.2327 15.0472 13.4064 14.971C13.4125 14.9695 13.5101 14.9146 13.6046 14.8628C13.6046 14.8918 13.6031 14.9375 13.6031 14.9375C13.632 16.9314 13.7464 18.3795 13.9644 19.4969L13.9674 19.5167L13.9888 19.5609C14.0955 19.7179 14.2174 20.1905 14.3256 20.6066L14.5741 21.4389C14.5802 21.4481 14.6442 21.5167 14.6442 21.5167C14.786 21.6127 15.2174 21.7788 15.5055 21.7453L15.5771 21.7362L15.6229 21.6356C15.6473 21.5106 15.6564 21.3886 15.6564 21.2545C15.6564 21.102 15.6457 20.9359 15.632 20.7301L15.6122 20.4298C15.5207 19.038 15.4293 17.0289 15.4293 15.6936V15.5869C15.6671 15.7347 16.7006 16.3856 16.7006 16.3856C18.4612 17.5274 21.1182 19.253 22.7264 19.7088C22.8103 19.7347 22.8971 19.7149 22.9596 19.6554C22.9612 19.6524 23.2066 19.4969 23.2066 19.4969L23.2523 19.4786C23.3438 19.4344 23.4063 19.2789 23.417 19.1112C24.7706 19.0426 26.6593 18.5213 27.6699 17.2515C28.0617 16.7591 28.237 16.1555 28.237 15.6402C28.237 15.2164 28.1196 14.8521 27.9062 14.6509"
      fill="#F8B500"
    />
    <path
      d="M16.2111 25.9897V26.7275H17.9398V28.6451H17.3498V27.2381H13.8789V26.7275H15.6212V25.9897H13.5176V25.4805H17.3209V24.4607H13.9429V23.9531H17.9138V25.476H18.3361V25.9897H16.2111Z"
      fill="#7C7266"
    />
    <path
      d="M8.08203 23.9625H10.3899C11.4875 23.9625 12.0606 24.3268 12.0606 25.124C12.0606 25.6911 11.6658 26.0813 11.1384 26.1865V26.2063C11.8945 26.2963 12.2512 26.7917 12.2512 27.3496C12.2512 28.0508 11.7375 28.656 10.4631 28.656H8.08203V23.9609V23.9625ZM8.82897 25.9411H10.3518C10.9174 25.9411 11.335 25.7262 11.335 25.2262C11.335 24.7887 11.1292 24.4975 10.2649 24.4975H8.82897V25.9411ZM8.82897 28.1255H10.3046C11.2207 28.1255 11.4646 27.7688 11.4646 27.313C11.4646 26.7734 11.0911 26.4746 10.2664 26.4746H8.83049V28.1255H8.82897Z"
      fill="#7C7266"
    />
    <path
      d="M3 23.9609H3.74999V26.063L6.27281 23.9609H7.33529L4.66766 26.0829L7.55633 28.6575H6.49537L3.74999 26.1408V28.6575H3V23.9609Z"
      fill="#7C7266"
    />
  </svg>
);

const IconFinance = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
  >
    <rect width="36" height="36" fill="white" />
    <path
      d="M23.4624 9.75H12.5451C7.18453 13.3711 3.5625 17.8754 3.5625 23.0774C3.5625 29.7986 9.1106 33.3755 17.9995 33.3755C26.8884 33.3755 32.4365 29.7986 32.4365 23.0774C32.4365 17.8754 28.8145 13.3711 23.4539 9.75883L23.4624 9.75Z"
      fill="#FF8442"
    />
    <path
      d="M22.7964 9.13763H13.2119L11.676 4.80168C11.3907 3.70463 12.146 2.625 13.1867 2.625H22.8132C23.8539 2.625 24.6093 3.71334 24.3239 4.80168L22.788 9.13763H22.7964Z"
      fill="#FF8442"
    />
    <path
      d="M23.0577 6.84375H12.9371C12.0895 6.84375 11.4023 7.55551 11.4023 8.4335V8.44234C11.4023 9.32033 12.0895 10.0321 12.9371 10.0321H23.0577C23.9053 10.0321 24.5924 9.32033 24.5924 8.44234V8.4335C24.5924 7.55551 23.9053 6.84375 23.0577 6.84375Z"
      fill="#943F12"
    />
    <path
      d="M24.0125 19.6905H23.1271L23.7994 17.7015C23.8814 17.468 23.865 17.2183 23.7502 16.9929C23.6354 16.7674 23.4469 16.6064 23.2173 16.5258C22.9796 16.4372 22.7172 16.4534 22.4959 16.558C22.2663 16.6708 22.0942 16.864 22.0122 17.0976L20.5037 21.4298L18.9296 17.0895C18.7985 16.7432 18.446 16.5178 18.036 16.5178C17.6261 16.5178 17.2736 16.7432 17.1342 17.0976L15.5684 21.462L14.0025 17.0895C13.9205 16.856 13.7402 16.6708 13.5188 16.558C13.2893 16.4453 13.0351 16.4292 12.7974 16.5178C12.2973 16.6949 12.0432 17.2183 12.2153 17.7095L12.9122 19.6824H11.9858C11.4775 19.6824 11.043 20.1092 11.043 20.6084C11.043 21.1077 11.4775 21.5345 11.9858 21.5345H13.5844L14.6748 24.6025C14.7978 24.9487 15.1503 25.1742 15.5684 25.1742C15.9865 25.1742 16.3308 24.9487 16.4702 24.5944L18.0278 20.2219L19.6183 24.6025C19.7413 24.9487 20.0938 25.1742 20.5037 25.1742C20.9136 25.1742 21.2743 24.9407 21.4055 24.5783L22.4549 21.5425H24.0043C24.5126 21.5425 24.9471 21.1158 24.9471 20.6165C24.9471 20.1172 24.5126 19.6905 24.0043 19.6905H24.0125Z"
      fill="white"
    />
  </svg>
);

const IconBuilding = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
  >
    <rect width="36" height="36" fill="white" />
    <path
      d="M12.75 5.85714C12.75 4.27919 14.0292 3 15.6071 3H26.0921C27.8362 3 29.25 4.41384 29.25 6.15789V33H12.75V5.85714Z"
      fill="#99A8B0"
    />
    <path
      d="M6.75 11.8571C6.75 10.2792 8.02919 9 9.60714 9H20.0921C21.8362 9 23.25 10.4138 23.25 12.1579V33H6.75V11.8571Z"
      fill="#C7D0D6"
    />
    <rect x="11.5508" y="12.8984" width="2.4" height="2.4" fill="#718189" />
    <rect x="16.0527" y="12.8984" width="2.4" height="2.4" fill="#718189" />
    <rect x="11.5508" y="17.4062" width="2.4" height="2.4" fill="#718189" />
    <rect x="16.0527" y="17.4062" width="2.4" height="2.4" fill="#718189" />
    <rect x="11.5508" y="21.9062" width="2.4" height="2.4" fill="#718189" />
    <rect x="16.0527" y="21.9062" width="2.4" height="2.4" fill="#718189" />
    <path
      d="M12.9004 29.3969C12.9004 28.2371 13.8406 27.2969 15.0004 27.2969C16.1602 27.2969 17.1004 28.2371 17.1004 29.3969V32.9969H12.9004V29.3969Z"
      fill="#718189"
    />
  </svg>
);

const IconPeople = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
  >
    <rect width="36" height="36" fill="white" />
    <path
      d="M12.7133 19.6516C18.4733 19.6516 23.2583 24.2266 23.9633 30.1516H4.04327C2.63327 30.1516 1.64327 28.7566 2.10827 27.4366C3.72827 22.8766 7.86827 19.6516 12.7133 19.6516ZM25.6133 21.7516C29.2283 21.7516 32.3633 24.0166 33.8183 27.2716C34.4333 28.6216 33.4433 30.1516 31.9583 30.1516H26.3933L26.3633 29.8666C26.0033 26.9116 24.8033 24.2566 23.0333 22.1566C23.8583 21.9016 24.7283 21.7516 25.6283 21.7516H25.6133ZM25.7633 9.45156C28.4933 9.45156 30.7133 11.6716 30.7133 14.4016C30.7133 17.1316 28.4933 19.3516 25.7633 19.3516C23.0333 19.3516 20.8133 17.1316 20.8133 14.4016C20.8133 11.6716 23.0333 9.45156 25.7633 9.45156ZM12.7133 5.85156C15.8633 5.85156 18.4133 8.40156 18.4133 11.5516C18.4133 14.7016 15.8633 17.2516 12.7133 17.2516C9.56327 17.2516 7.01327 14.7016 7.01327 11.5516C7.01327 8.40156 9.56327 5.85156 12.7133 5.85156Z"
      fill="#69A7FF"
    />
  </svg>
);

const IconStar = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
  >
    <rect width="36" height="36" fill="white" />
    <path
      d="M16.0149 3.94718C16.8768 2.08989 19.5114 2.07734 20.3909 3.92634L23.9342 11.3751L31.6989 12.3797C33.6889 12.6372 34.5175 15.0679 33.0994 16.4883L27.3763 22.2207L28.8417 30.9999C29.1289 32.7205 27.313 34.0191 25.7788 33.1904L18.1807 29.0857L10.5827 33.1904C9.04849 34.0191 7.23261 32.7205 7.5198 30.9999L8.98518 22.2207L2.80496 16.0304C1.57478 14.7983 2.29521 12.6896 4.02184 12.4686L12.5678 11.3751L16.0149 3.94718Z"
      fill="#FF7225"
    />
  </svg>
);

function FillBox({ children }: { children: React.ReactNode }) {
  return (
    <Box bg={baseColors.fill_normal} borderRadius="12px" px="20px" py="16px">
      {children}
    </Box>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <BaseStack spacing={8}>
      {items.map((item, i) => (
        <BaseStack key={i} direction="horizontal" spacing={8}>
          <Box
            flexShrink={0}
            className="body_2_normal_r"
            color={baseColors.label_alternative_1}
          >
            •
          </Box>
          <Box
            className="body_2_normal_r"
            color={baseColors.label_alternative_1}
          >
            {item}
          </Box>
        </BaseStack>
      ))}
    </BaseStack>
  );
}

function TrustItem({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <BaseStack spacing={16} width="100%">
      {/* 48px 흰 배경 박스 */}
      <Box
        width="48px"
        height="48px"
        bg={colors.white}
        borderRadius="6px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        {icon}
      </Box>
      <Box>
        <Box
          className="headline_3_sb"
          css={responsive(PC_CRITERION_TITLE)}
          color={baseColors.label_normal}
          mb="4px"
        >
          {title}
        </Box>
        <Box
          className="body_1_reading_r"
          css={responsive(PC_BODY)}
          color={baseColors.label_alternative_1}
        >
          {body}
        </Box>
      </Box>
    </BaseStack>
  );
}

export default function PrepareThird() {
  const router = useRouter();

  const goConsult = () => {
    customEvent({
      newGtm: { path: "prepare_content", trigger_and_target: "btn_consult" },
    });
    call("1666-9784");
  };

  const go100won = () => {
    customEvent({
      newGtm: { path: "prepare_content", trigger_and_target: "btn_100won" },
    });
    router.push("/?utm_campaign=prepare_3_100won");
  };

  const goSangjoJoin = () => {
    customEvent({
      newGtm: { path: "prepare_content", trigger_and_target: "article_sangjo" },
    });
    router.push("/content/feeds/sangjo-join/");
  };

  return (
    <BaseStack width="100%" alignItems="center">
      {/* Hero — 모바일: 이미지만, PC: 이미지 + 타이틀 오버레이 */}
      <Box width="100%" position="relative">
        {/* 모바일: 이미지 그대로 표시 (타이틀·오버레이 없음) */}
        <Box
          css={responsive({ display: { base: "block", lg: "none" } })}
          width="100%"
          overflow="hidden"
        >
          <Image
            src={getCdnImageSrc(
              "direct/content/feed/good-funeral-mobile-header.png"
            )}
            alt=""
            width="100%"
          />
        </Box>

        {/* PC: 다크 배경 + 이미지 + 타이틀 오버레이 */}
        <Box
          css={responsive({ display: { base: "none", lg: "block" } })}
          position="relative"
          height="400px"
          bg="#4c565e"
          overflow="hidden"
        >
          <Box position="absolute" inset="0" overflow="hidden">
            <Image
              src={getCdnImageSrc(
                "direct/content/feed/good-funeral-header.png"
              )}
              alt=""
              width="100%"
              height="100%"
              css={{ objectFit: "cover" }}
            />
          </Box>
          {/* 그라디언트 오버레이: 하단 dark → 상단 transparent */}
          <Box
            position="absolute"
            inset="0"
            css={{
              backgroundImage:
                "linear-gradient(0deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 100%)",
            }}
          />
          <Box position="absolute" bottom="40px" left="calc(50% - 340px)">
            <Box
              css={{
                fontSize: "56px",
                fontWeight: 500,
                lineHeight: "76px",
                letterSpacing: "-1px",
              }}
              color={colors.white}
              whiteSpace="pre-line"
            >
              {"좋은 상조 회사,\n이렇게 고르세요"}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Stack
        maxWidth="720px"
        width="100%"
        css={responsive({
          padding: { base: "32px 20px 80px", lg: "60px 0 100px" },
        })}
      >
        <BaseStack spacing={56}>
          {/* ── 모바일 전용 타이틀 (히어로 아래) ── */}
          <Box
            css={responsive({ display: { base: "block", lg: "none" } })}
            className="title_2_m"
            color={baseColors.label_normal}
            whiteSpace="pre-line"
          >
            {"좋은 상조 회사,\n이렇게 고르세요"}
          </Box>

          {/* ── 도입 + 4가지 기준 ── */}
          <BaseStack spacing={40}>
            <Box
              className="body_1_reading_r"
              css={responsive(PC_BODY)}
              color={baseColors.label_alternative_1}
            >
              상조는 일찍 준비할수록 좋은 서비스지만, 한 번 가입하면 쉽게 바꾸기
              어려운 서비스이기도 합니다.
              <br />
              어떤 상조 회사가 좋은지 비교할 때 꼭 확인해야 할 4가지 기준을
              정리해드립니다.
            </Box>

            <Box
              className="headline_1_sb"
              css={responsive(PC_SECTION_TITLE)}
              color={baseColors.label_normal}
            >
              좋은 상조 회사를 고르는 4가지 기준
            </Box>

            {DIVIDER}

            {/* 기준 1~4 */}
            <BaseStack spacing={32}>
              <BaseStack spacing={20}>
                <BaseStack spacing={8}>
                  <Box
                    className="headline_3_sb"
                    css={responsive(PC_CRITERION_TITLE)}
                    color={baseColors.label_normal}
                  >
                    1. 상품 구성과 가격이 투명한가
                  </Box>
                  <Box
                    className="body_1_reading_r"
                    css={responsive(PC_BODY)}
                    color={baseColors.label_alternative_1}
                  >
                    장례에 꼭 필요한 물품이 상품에서 빠져있을 경우, 현장에서
                    비싼 값을 주고 추가 구매해야 합니다.
                    <br />
                    가입 전에 다음을 확인하세요.
                  </Box>
                </BaseStack>
                <FillBox>
                  <BulletList
                    items={[
                      "상품별 가격이 정찰제로 투명하게 공개되어 있는가",
                      "수의, 유골함 등 장례에 반드시 필요한 품목이 모두 포함되어 있는가",
                    ]}
                  />
                </FillBox>
              </BaseStack>

              <BaseStack spacing={20}>
                <BaseStack spacing={8}>
                  <Box
                    className="headline_3_sb"
                    css={responsive(PC_CRITERION_TITLE)}
                    color={baseColors.label_normal}
                  >
                    2. 재정적으로 안정된 회사인가
                  </Box>
                  <Box
                    className="body_1_reading_r"
                    css={responsive(PC_BODY)}
                    color={baseColors.label_alternative_1}
                  >
                    상조 회사가 폐업하면 계약한 서비스는 물론, 납입한 돈도
                    돌려받지 못할 수 있습니다.
                    <br />
                    실제로{" "}
                    <Box as="span" fontWeight={600}>
                      최근 3년간 8개 상조회사가 폐업
                    </Box>
                    했으며, 그로 인해 돌려받지 못한 금액이 약 300억 원에
                    달합니다. (매일경제, 2024)
                  </Box>
                </BaseStack>
                <FillBox>
                  <BulletList
                    items={[
                      "월 납입금이 적어 위험 부담이 낮은 상품인가",
                      "재무 건전성을 확인할 수 있는 지표가 공개되어 있는가",
                      "선수금이 안전하게 보관되는 구조인가",
                    ]}
                  />
                </FillBox>
              </BaseStack>

              <BaseStack spacing={20}>
                <BaseStack spacing={8}>
                  <Box
                    className="headline_3_sb"
                    css={responsive(PC_CRITERION_TITLE)}
                    color={baseColors.label_normal}
                  >
                    3. 해지 조건이 명확하고 합리적인가
                  </Box>
                  <Box
                    className="body_1_reading_r"
                    css={responsive(PC_BODY)}
                    color={baseColors.label_alternative_1}
                  >
                    상조사 대부분이 선수금 운용을 수익 모델로 삼기 때문에,{" "}
                    <Box as="span" fontWeight={600}>
                      해지 시 위약금이 크거나 납입금의 절반도 돌려받지 못하는
                      경우
                    </Box>
                    가 많습니다.
                    <br />
                    가입은 전화로 했는데 해지는 직접 방문해야 하는 등 불투명한
                    조건도 흔합니다.
                  </Box>
                </BaseStack>
                <FillBox>
                  <BulletList
                    items={[
                      "위약금 없이 해지가 가능한가",
                      "환불 기준이 명확히 안내되어 있는가",
                      "환불 절차가 간단하고 빠른가",
                    ]}
                  />
                </FillBox>
              </BaseStack>

              <BaseStack spacing={20}>
                <BaseStack spacing={8}>
                  <Box
                    className="headline_3_sb"
                    css={responsive(PC_CRITERION_TITLE)}
                    color={baseColors.label_normal}
                  >
                    4. 실제 이용자 후기가 충분한가
                  </Box>
                  <Box
                    className="body_1_reading_r"
                    css={responsive(PC_BODY)}
                    color={baseColors.label_alternative_1}
                  >
                    서비스 품질은 결국 실제 이용자의 후기로만 확인할 수
                    있습니다.
                    <br />
                    후기가 적거나 평가가 일관되지 않다면 주의가 필요합니다.
                  </Box>
                </BaseStack>
                <FillBox>
                  <BulletList
                    items={[
                      "후기 수가 충분히 많은가",
                      "평균 평점이 높은가",
                      "추가 비용, 불친절 등 부정적 패턴이 반복되지 않는가",
                    ]}
                  />
                </FillBox>
              </BaseStack>
            </BaseStack>

            {DIVIDER}

            {/* 요약 */}
            <BaseStack spacing={20}>
              <Box
                className="headline_1_sb"
                css={responsive(PC_SECTION_TITLE)}
                color={baseColors.label_normal}
              >
                4가지 기준, 정리하면 이렇습니다
              </Box>
              <FillBox>
                <BulletList
                  items={[
                    "필수 항목이 빠짐없이 포함된 정찰제 상품",
                    "월 납입금이 적고 재무가 안정적인 회사",
                    "해지 위약금이 없고 환불 조건이 명확한 상품",
                    "후기가 많고 평가가 높은 서비스",
                  ]}
                />
              </FillBox>
              <Box
                className="body_1_reading_r"
                css={responsive(PC_BODY)}
                color={baseColors.label_alternative_1}
              >
                이 4가지를 모두 충족하는 상조 회사를 찾는다면, 후회 없는 선택을
                하실 수 있습니다.
              </Box>
            </BaseStack>
          </BaseStack>

          {DIVIDER}

          {/* ── 고이 100원 상조 ── */}
          <BaseStack spacing={20}>
            <BaseStack spacing={8}>
              <Box
                className="headline_1_sb"
                css={responsive({
                  ...PC_SECTION_TITLE,
                  whiteSpace: "pre-line",
                })}
                color={baseColors.label_normal}
              >
                {"4가지 기준을 모두 충족하는 상조:\n고이 100원 상조"}
              </Box>
              <Box
                className="body_1_reading_r"
                css={responsive(PC_BODY)}
                color={baseColors.label_alternative_1}
              >
                고이 100원 상조는 위 4가지 기준을 모두 충족하도록 설계된
                상품입니다.
              </Box>
            </BaseStack>

            {/* 다크 2열 테이블 */}
            <Box width="100%" border="1px solid #dddedf" overflow="hidden">
              {[
                {
                  label: "상품 구성",
                  content: "정찰제 운영, 수의·유골함 등 필수 품목 모두 포함",
                },
                {
                  label: "재정 안정성",
                  content:
                    "업계 최초 벤처투자 119억+, 월 납입금 100원으로 위험 부담 최소화,\n지급여력비율 2,003%",
                },
                {
                  label: "해지 조건",
                  content: "위약금 없이 언제든 100% 환급 가능",
                },
                {
                  label: "후기",
                  content: "평균 만족도 4.9 / 5.0, 누적 18만 가족 이용",
                },
              ].map((row, i, arr) => (
                <BaseStack
                  key={i}
                  direction="horizontal"
                  css={{
                    borderBottom:
                      i < arr.length - 1 ? "1px solid #dddedf" : "none",
                  }}
                >
                  <Box
                    bg="#36383a"
                    width="25%"
                    flexShrink={0}
                    px="12px"
                    py="16px"
                    className="label_1_sb"
                    css={responsive(PC_TABLE_LABEL)}
                    color={colors.white}
                    display="flex"
                    alignItems="center"
                  >
                    {row.label}
                  </Box>
                  <Box
                    bg={colors.white}
                    flex={1}
                    px="12px"
                    py="16px"
                    className="body_1_reading_r"
                    css={responsive(PC_BODY)}
                    color={baseColors.label_alternative_1}
                    display="flex"
                    alignItems="center"
                    whiteSpace="pre-line"
                  >
                    {row.content}
                  </Box>
                </BaseStack>
              ))}
            </Box>
          </BaseStack>

          {/* ── 왜 100원인가 (DIVIDER 없음) ── */}
          <BaseStack spacing={20}>
            <Box
              className="headline_1_sb"
              css={responsive(PC_SECTION_TITLE)}
              color={baseColors.label_normal}
            >
              {"왜 '월 100원'인가요?"}
            </Box>
            <Box
              className="body_1_reading_r"
              css={responsive(PC_BODY)}
              color={baseColors.label_alternative_1}
            >
              선불제 상조는 보통 월 3만~5만 원씩 약 120회를 납입해 총
              400만~600만 원이 됩니다.
              <br />큰 목돈을 장기간 묶어두는 부담이 있고, 회사가 폐업하면 그
              돈을 잃을 위험도 큽니다.
            </Box>
            <Box
              className="body_1_reading_r"
              css={responsive(PC_BODY)}
              color={baseColors.label_alternative_1}
            >
              고이 100원 상조는{" "}
              <Box as="span" fontWeight={600}>
                선불제지만 월 납입금이 100원
              </Box>
              입니다.
              <br />
              가입 시점의 장례 가격을 평생 보장받으면서도, 목돈이 묶이거나 잃을
              위험은 거의 없습니다.
            </Box>

            {/* 4열 비교표 */}
            <Box width="100%" overflowX="auto" border="1px solid #dddedf">
              <Box minWidth="280px">
                <Box
                  display="grid"
                  gridTemplateColumns="repeat(4, 1fr)"
                  bg="#36383a"
                >
                  {["구분", "기존 선불제", "후불제", "100원 상조"].map(
                    (h, i) => (
                      <Box
                        key={i}
                        px="8px"
                        py="10px"
                        className="label_1_sb"
                        css={responsive(PC_TABLE_LABEL)}
                        color={colors.white}
                        textAlign="center"
                      >
                        {h}
                      </Box>
                    )
                  )}
                </Box>
                {[
                  {
                    label: "월 납입금",
                    col1: "3만~5만 원",
                    col2: "없음",
                    col3: "월 100원",
                  },
                  {
                    label: "가격\n평생 보장",
                    col1: "가능",
                    col2: "불가능",
                    col3: "가능",
                  },
                  {
                    label: "폐업 시\n손실 위험",
                    col1: "큼",
                    col2: "없음",
                    col3: "없음",
                  },
                  {
                    label: "중도 해지\n부담",
                    col1: "위약금 발생/\n환급률 낮음",
                    col2: "-",
                    col3: "위약금 없음",
                  },
                ].map((row, i, arr) => (
                  <Box
                    key={i}
                    display="grid"
                    gridTemplateColumns="repeat(4, 1fr)"
                  >
                    {/* 첫 열: bold, 모바일 pre-line → PC normal */}
                    <Box
                      px="8px"
                      py="10px"
                      className="label_1_sb"
                      css={responsive({
                        ...PC_TABLE_CELL,
                        whiteSpace: { base: "pre-line", lg: "normal" },
                        borderBottom:
                          i < arr.length - 1 ? "1px solid #dddedf" : "none",
                      })}
                      color={baseColors.label_alternative_1}
                      textAlign="center"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {row.label}
                    </Box>
                    <Box
                      px="8px"
                      py="10px"
                      className="label_1_r"
                      css={responsive({
                        ...PC_TABLE_CELL,
                        whiteSpace: { base: "pre-line", lg: "normal" },
                        borderBottom:
                          i < arr.length - 1 ? "1px solid #dddedf" : "none",
                      })}
                      color={baseColors.label_alternative_1}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      textAlign="center"
                    >
                      {row.col1}
                    </Box>
                    <Box
                      px="8px"
                      py="10px"
                      className="label_1_r"
                      css={responsive({
                        ...PC_TABLE_CELL,
                        borderBottom:
                          i < arr.length - 1 ? "1px solid #dddedf" : "none",
                      })}
                      color={baseColors.label_alternative_1}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      textAlign="center"
                    >
                      {row.col2}
                    </Box>
                    {/* 4열: 노란 배경 + 다른 열과 동일한 구분선 */}
                    <Box
                      px="8px"
                      py="10px"
                      className="label_1_sb"
                      css={responsive({
                        ...PC_TABLE_CELL,
                        borderBottom:
                          i < arr.length - 1 ? "1px solid #dddedf" : "none",
                      })}
                      color={colors.orange650}
                      textAlign="center"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bg="rgba(255, 241, 215, 0.7)"
                    >
                      {row.col3}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </BaseStack>

          {DIVIDER}

          {/* ── 믿고 맡길 수 있는 이유 ── */}
          <BaseStack spacing={24}>
            <BaseStack spacing={8}>
              <Box
                className="headline_1_sb"
                css={responsive(PC_SECTION_TITLE)}
                color={baseColors.label_normal}
              >
                믿고 맡길 수 있는 이유
              </Box>
              <Box
                className="body_1_reading_r"
                css={responsive(PC_BODY)}
                color={baseColors.label_alternative_1}
              >
                상조는 길게는 수십 년에 걸쳐 이어지는 서비스로 가입 전에 충분한
                고민이 필요합니다.
                <br />
                고이 100원 상조를 신뢰할 수 있는지 판단하실 수 있도록 고이가
                받은 검증과 운영 현황을 정리해드립니다.
              </Box>
            </BaseStack>

            {/* [정부·공공기관의 검증] */}
            <BaseStack spacing={12}>
              <Box
                className="headline_3_sb"
                css={responsive({
                  fontSize: { lg: "21px" },
                  lineHeight: { lg: "28px" },
                })}
                color={baseColors.label_normal}
              >
                [정부·공공기관의 검증]
              </Box>
              <FillBox>
                <BaseStack spacing={16}>
                  <TrustItem
                    icon={
                      <Image
                        src={getCdnImageSrc("direct/content/feed/ccm.png")}
                        alt="CCM"
                        width="36px"
                        height="36px"
                      />
                    }
                    title="소비자중심경영(CCM) 인증"
                    body={
                      <>
                        공정거래위원회와 한국소비자원이 운영하는 인증으로,
                        운영이 부실하면 받을 수 없습니다.
                        <br />
                        CCM을 받은 또 다른 기업엔 삼성전자·LG전자 등이 있습니다.
                      </>
                    }
                  />
                  {TRUST_DIVIDER}
                  <TrustItem
                    icon={<IconKB />}
                    title="제1금융권 선수금 예치"
                    body="납입하신 선수금은 KB국민은행에 예치되며, 상조사가 임의로 사용할 수 없는 구조로 안전하게 보관됩니다."
                  />
                </BaseStack>
              </FillBox>
            </BaseStack>

            {/* [재무 안정성] */}
            <BaseStack spacing={12}>
              <Box
                className="headline_3_sb"
                css={responsive({
                  fontSize: { lg: "21px" },
                  lineHeight: { lg: "28px" },
                })}
                color={baseColors.label_normal}
              >
                [재무 안정성]
              </Box>
              <FillBox>
                <BaseStack spacing={16}>
                  <TrustItem
                    icon={<IconFinance />}
                    title="지급여력비율 2,003%"
                    body="약속된 장례 서비스를 실제로 제공할 수 있는 재정적 능력을 나타내는 지표로, 업계 평균 대비 약 10배 수준입니다."
                  />
                  {TRUST_DIVIDER}
                  <TrustItem
                    icon={<IconBuilding />}
                    title="누적 투자 유치 119억 원"
                    body="업계 최초로 벤처투자를 유치한 상조사입니다."
                  />
                </BaseStack>
              </FillBox>
            </BaseStack>

            {/* [시장의 선택] */}
            <BaseStack spacing={12}>
              <Box
                className="headline_3_sb"
                css={responsive({
                  fontSize: { lg: "21px" },
                  lineHeight: { lg: "28px" },
                })}
                color={baseColors.label_normal}
              >
                [시장의 선택]
              </Box>
              <FillBox>
                <BaseStack spacing={16}>
                  <TrustItem
                    icon={<IconPeople />}
                    title="전국 18만 가족의 선택"
                    body="누적 18만 가족이 고이의 장례 서비스를 이용하셨습니다."
                  />
                  {TRUST_DIVIDER}
                  <TrustItem
                    icon={
                      <Image
                        src={getCdnImageSrc("direct/content/feed/kbba.png")}
                        alt="KBBA"
                        width="36px"
                        height="36px"
                      />
                    }
                    title="5년 연속 대한민국 최고브랜드 대상"
                    body="장례 서비스 부문에서 5년 연속 수상했습니다."
                  />
                  {TRUST_DIVIDER}
                  <TrustItem
                    icon={<IconStar />}
                    title="평균 만족도 4.9"
                    body="실제 장례를 진행한 유가족이 직접 매긴 점수와 후기를 확인해 보세요."
                  />
                </BaseStack>
              </FillBox>
            </BaseStack>
          </BaseStack>

          {/* ── FAQ ── */}
          <BaseStack spacing={16}>
            <Box
              className="headline_1_sb"
              css={responsive(PC_SECTION_TITLE)}
              color={baseColors.label_normal}
            >
              자주 묻는 질문
            </Box>
            <Accordion allowToggle>
              <Box bg={baseColors.fill_normal} overflow="hidden">
                {[
                  {
                    q: "100원 상조는 어떤 구조인가요?",
                    a: "선불제 상조입니다. 월 납입금이 100원이라 후불제처럼 부담이 적으면서도, 선불제의 가격 평생 보장 혜택을 그대로 누리실 수 있습니다.",
                  },
                  {
                    q: "다른 상조 회사에 이미 가입했는데도 추가 가입이 가능한가요?",
                    a: "중복 가입 가능합니다. 기존 상조의 환급률이 낮은 경우 해지하지 않고 백업으로 가입하시는 분들도 많습니다.",
                  },
                  {
                    q: "가입 후 해지하면 어떻게 되나요?",
                    a: "위약금 없이 언제든 해지 가능합니다.",
                  },
                  {
                    q: "장례가 발생했을 때 어떻게 연락하나요?",
                    a: "24시간 콜센터(1666-9784)로 연락주시면 즉시 출동합니다.",
                  },
                ].map((item, i, arr) => (
                  <AccordionItem
                    key={i}
                    border="none"
                    css={{
                      borderBottom:
                        i < arr.length - 1 ? "1px solid #dddedf" : "none",
                    }}
                  >
                    <AccordionButton
                      px="20px"
                      py="16px"
                      _hover={{ bg: "transparent" }}
                      gap="8px"
                    >
                      <Box
                        flex={1}
                        textAlign="left"
                        className="body_2_normal_m"
                        css={responsive(PC_FAQ)}
                        color={baseColors.label_normal}
                      >
                        {item.q}
                      </Box>
                      <AccordionIcon color={baseColors.label_alternative_1} />
                    </AccordionButton>
                    <AccordionPanel px="20px" pt="0" pb="16px">
                      <Box
                        className="body_1_reading_r"
                        color={baseColors.label_alternative_1}
                      >
                        {item.a}
                      </Box>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Box>
            </Accordion>
          </BaseStack>

          {/* ── CTA 버튼 — 모바일 상하, PC 좌우 ── */}
          <Box
            display="flex"
            css={responsive({
              flexDirection: { base: "column", lg: "row" },
              gap: "8px",
            })}
          >
            <Box
              flex={1}
              border={`1px solid ${baseColors.line_normal}`}
              borderRadius="6px"
              cursor="pointer"
              onClick={goConsult}
              px="12px"
              py="10px"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Box
                className="body_2_normal_m"
                color={baseColors.label_alternative_1}
                textAlign="center"
              >
                전문가와 상담 받아보기
              </Box>
            </Box>
            <Box
              flex={1}
              bg={baseColors.primary_orange}
              borderRadius="6px"
              cursor="pointer"
              onClick={go100won}
              px="12px"
              py="10px"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Box
                className="body_2_normal_m"
                color={colors.white}
                textAlign="center"
              >
                100원 상조 더 알아보기
              </Box>
            </Box>
          </Box>

          {/* ── 다른 컨텐츠 보기 ── */}
          <Box
            display="flex"
            flexDirection="row"
            bg={baseColors.fill_normal}
            borderRadius="6px"
            overflow="hidden"
            alignItems="center"
            css={responsive({
              gap: { base: "16px", lg: "20px" },
              paddingRight: "20px",
              paddingLeft: { base: "20px", lg: "0" },
            })}
            cursor="pointer"
            onClick={goSangjoJoin}
          >
            {/* 썸네일: PC만 표시, 116×88 flush */}
            <Box
              css={responsive({ display: { base: "none", lg: "block" } })}
              width="116px"
              height="88px"
              flexShrink={0}
              bg="#e8e9ea"
              overflow="hidden"
            >
              <Image
                src={getCdnImageSrc(
                  "direct/content/feed/sangjo-join-thumbnail.png"
                )}
                alt=""
                width="100%"
                height="100%"
                css={{ objectFit: "cover", display: "block" }}
              />
            </Box>
            {/* 텍스트: py는 여기에 적용 (이미지는 flush) */}
            <Box flex={1} py="12px">
              <Box
                className="body_2_reading_r"
                css={responsive({
                  fontSize: { lg: "16px" },
                  lineHeight: { lg: "24px" },
                })}
                color={baseColors.label_alternative_1}
              >
                다른 컨텐츠 보기
              </Box>
              <Box
                className="body_1_normal_sb"
                css={responsive({
                  fontSize: { lg: "18px" },
                  lineHeight: { lg: "26px" },
                  letterSpacing: { lg: "-0.2px" },
                })}
                color={baseColors.label_normal}
              >
                상조 서비스, 한 번에 정리해드립니다.
              </Box>
            </Box>
            <ColorIcon
              name="arrow_right_small"
              size={16}
              color={baseColors.label_alternative_1}
            />
          </Box>
        </BaseStack>
      </Stack>

      <Box width="100%" height="1px" bg={baseColors.line_normal} />
      <RenewalFooter />
    </BaseStack>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      pageOgTitle: "상조 추천 | 좋은 상조 회사 고르는 4가지 기준",
      pageDescription:
        "상조 가입 전 꼭 확인할 4가지 기준과, 모두 충족하는 고이 100원 상조를 소개합니다. 정찰제, 위약금 없는 해지, 누적 18만 가족 이용.",
      pageOgDescription:
        "상조 가입 전 꼭 확인할 4가지 기준과, 모두 충족하는 고이 100원 상조를 소개합니다. 정찰제, 위약금 없는 해지, 누적 18만 가족 이용.",
      pageKeywords:
        "상조, 상조 추천, 상조 회사, 상조 비교, 상조 가입, 좋은 상조, 100원 상조, 고이 프라임",
    },
  };
};
